import { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { updateCoil, listMasters } from "@/lib/api";
import { toast } from "sonner";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Save, Loader2 } from "lucide-react";

const Row = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-semibold tracking-wide uppercase text-slate-600">
      {label}
    </Label>
    {children}
  </div>
);

const EditRMDialog = ({ coil, open, onOpenChange }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (coil) {
      setForm({
        grade: coil.grade || "",
        weight_kg: coil.weight_kg ?? "",
        gross_weight_kg: coil.gross_weight_kg ?? "",
        thickness_mm: coil.thickness_mm ?? "",
        width_mm: coil.width_mm ?? "",
        supplier: coil.supplier || "",
        date_received: coil.date_received || "",
        notes: coil.notes || "",
      });
    }
  }, [coil]);

  // Same source + cache key as CoilForm: active RM Grade masters only.
  const { data: grades = [] } = useQuery({
    queryKey: ["masters", "rm_grade", "active"],
    queryFn: async () => {
      const data = await listMasters({ master_type: "rm_grade", status: "active" });
      return (Array.isArray(data) ? data : []).map((m) => m.name).filter(Boolean).sort();
    },
    staleTime: 60_000,
  });
  const gradeInMasters = useMemo(() => grades.includes(form?.grade), [grades, form?.grade]);

  if (!form) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Save disabled until the user actually changes something.
  const dirty = (
    form.grade !== (coil.grade || "")
    || Number(form.weight_kg) !== Number(coil.weight_kg)
    || Number(form.gross_weight_kg || 0) !== Number(coil.gross_weight_kg || 0)
    || Number(form.thickness_mm) !== Number(coil.thickness_mm)
    || Number(form.width_mm) !== Number(coil.width_mm)
    || form.supplier !== (coil.supplier || "")
    || form.date_received !== (coil.date_received || "")
    || form.notes !== (coil.notes || "")
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch = {
        grade: form.grade,
        weight_kg: Number(form.weight_kg),
        gross_weight_kg: form.gross_weight_kg === "" || form.gross_weight_kg == null ? null : Number(form.gross_weight_kg),
        thickness_mm: Number(form.thickness_mm),
        width_mm: Number(form.width_mm),
        supplier: form.supplier,
        date_received: form.date_received,
        notes: form.notes,
      };
      await updateCoil(coil.id, patch);
      toast.success(`Coil ${coil.coil_id} updated`);
      queryClient.invalidateQueries({ queryKey: ["coils"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update coil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-rm-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Edit RM Coil <span className="font-mono-data text-slate-700">{coil.coil_id}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <Row id="edit-grade" label="Grade">
            <Select value={gradeInMasters ? form.grade : ""} onValueChange={(v) => set("grade", v)}>
              <SelectTrigger data-testid="edit-grade">
                <SelectValue placeholder={grades.length ? "Select grade" : "No grades in Masters — add one first"} />
              </SelectTrigger>
              <SelectContent>
                {grades.map((g) => (
                  <SelectItem key={g} value={g} data-testid={`edit-grade-option-${g}`}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.grade && !gradeInMasters && (
              <p data-testid="edit-grade-not-in-masters" className="text-[11px] text-amber-700">
                Current grade "{form.grade}" isn't in Masters → RM Grade. Add it there first, or pick one above.
              </p>
            )}
          </Row>
          <Row id="edit-supplier" label="Supplier">
            <Input id="edit-supplier" data-testid="edit-supplier" value={form.supplier} onChange={(e) => set("supplier", e.target.value)} />
          </Row>
          <Row id="edit-net-weight" label="Net Weight (kg)">
            <Input id="edit-net-weight" data-testid="edit-net-weight" type="number" step="0.01" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className="font-mono-data" />
          </Row>
          <Row id="edit-gross-weight" label="Gross Weight (kg)">
            <Input id="edit-gross-weight" data-testid="edit-gross-weight" type="number" step="0.01" value={form.gross_weight_kg} onChange={(e) => set("gross_weight_kg", e.target.value)} className="font-mono-data" />
          </Row>
          <Row id="edit-thickness" label="Thickness (mm)">
            <Input id="edit-thickness" data-testid="edit-thickness" type="number" step="0.01" value={form.thickness_mm} onChange={(e) => set("thickness_mm", e.target.value)} className="font-mono-data" />
          </Row>
          <Row id="edit-width" label="Width (mm)">
            <Input id="edit-width" data-testid="edit-width" type="number" step="0.1" value={form.width_mm} onChange={(e) => set("width_mm", e.target.value)} className="font-mono-data" />
          </Row>
          <Row id="edit-date" label="Date Received">
            <Input id="edit-date" data-testid="edit-date-received" type="date" value={form.date_received || ""} onChange={(e) => set("date_received", e.target.value)} />
          </Row>
          <div className="sm:col-span-2">
            <Row id="edit-notes" label="Notes">
              <Textarea id="edit-notes" data-testid="edit-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </Row>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-edit-btn" className="rounded-sm">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            data-testid="save-edit-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> Save changes</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRMDialog;
